/**
 * useResortNotices — owns the "Resorting by …" notice lifecycle for the
 * pipeline board: the two-phase fade-out timer sequence, per-item
 * cancellation on re-drag, and the unmount cleanup for pending timers.
 *
 * Extracted from PipelineBoard, which still owns `RESORT_NOTICE_MS` /
 * `RESORT_NOTICE_FADE_MS` (passed in) alongside the documented invariant
 * that they must stay greater than `HIGHLIGHT_MS` — see PipelineBoard.tsx.
 *
 * Two-phase teardown, both timers keyed to the same map slot per item:
 *   1. After `resortNoticeMs - resortNoticeFadeMs`, the item enters
 *      `exitingNoticeIds` — the badge stays mounted and plays its fade-out.
 *   2. After another `resortNoticeFadeMs`, the item is dropped from both
 *      `resortNotices` and `exitingNoticeIds` — the badge unmounts.
 * Re-triggering a notice for the same item (re-drag) cancels any pending
 * timer for that item first, so only one phase is ever in flight per item.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface ResortNoticesResult {
  /** Item id -> target stage id, for items with a live "Resorting by …" notice. */
  resortNotices: Map<string | number, string>;
  /** Item ids currently in their fade-out phase (still mounted). */
  exitingNoticeIds: Set<string | number>;
  /**
   * Start (or restart) the notice for an item that just re-sorted into
   * `stage`. Schedules the two-phase fade-out teardown.
   */
  triggerResortNotice: (itemId: string | number, stage: string) => void;
  /** Drop a pending notice for an item (e.g. re-dragging the same card). */
  cancelResortNotice: (itemId: string | number) => void;
  /** Clear every notice — used when an API failure rolls a move back. */
  cancelAllResortNotices: () => void;
}

export function useResortNotices(
  resortNoticeMs: number,
  resortNoticeFadeMs: number
): ResortNoticesResult {
  const [resortNotices, setResortNotices] = useState<
    Map<string | number, string>
  >(new Map());
  const noticeTimersRef = useRef<
    Map<string | number, ReturnType<typeof setTimeout>>
  >(new Map());
  const [exitingNoticeIds, setExitingNoticeIds] = useState<
    Set<string | number>
  >(new Set());

  const cancelResortNotice = useCallback((itemId: string | number) => {
    const noticeTimer = noticeTimersRef.current.get(itemId);
    if (noticeTimer) {
      clearTimeout(noticeTimer);
      noticeTimersRef.current.delete(itemId);
    }
    setResortNotices(prev => {
      if (!prev.has(itemId)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
    setExitingNoticeIds(prev => {
      if (!prev.has(itemId)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(itemId);
      return next;
    });
  }, []);

  const cancelAllResortNotices = useCallback(() => {
    for (const timer of noticeTimersRef.current.values()) {
      clearTimeout(timer);
    }
    noticeTimersRef.current.clear();
    setResortNotices(new Map());
    setExitingNoticeIds(new Set());
  }, []);

  // Clear all pending timers on unmount.
  useEffect(() => {
    const noticeTimers = noticeTimersRef.current;
    return () => {
      for (const timer of noticeTimers.values()) {
        clearTimeout(timer);
      }
      noticeTimers.clear();
    };
  }, []);

  const triggerResortNotice = useCallback(
    (itemId: string | number, stage: string) => {
      setResortNotices(prev => {
        const next = new Map(prev);
        next.set(itemId, stage);
        return next;
      });

      const existingNotice = noticeTimersRef.current.get(itemId);
      if (existingNotice) {
        clearTimeout(existingNotice);
      }
      // If the item was mid-fade, clear the exiting flag so the fresh notice
      // renders at full opacity instead of staying faded/hidden.
      setExitingNoticeIds(prev => {
        if (!prev.has(itemId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      // Two-phase teardown so the badge fades rather than pops. Both timers
      // live in the same map slot — only one is ever pending per item.
      noticeTimersRef.current.set(
        itemId,
        setTimeout(() => {
          // Phase 1: start the fade, keeping the badge mounted.
          setExitingNoticeIds(prev => new Set(prev).add(itemId));
          // Phase 2: unmount once the fade has played out.
          noticeTimersRef.current.set(
            itemId,
            setTimeout(() => {
              noticeTimersRef.current.delete(itemId);
              setResortNotices(prev => {
                const next = new Map(prev);
                next.delete(itemId);
                return next;
              });
              setExitingNoticeIds(prev => {
                const next = new Set(prev);
                next.delete(itemId);
                return next;
              });
            }, resortNoticeFadeMs)
          );
        }, resortNoticeMs - resortNoticeFadeMs)
      );
    },
    [resortNoticeMs, resortNoticeFadeMs]
  );

  return useMemo(
    () => ({
      resortNotices,
      exitingNoticeIds,
      triggerResortNotice,
      cancelResortNotice,
      cancelAllResortNotices,
    }),
    [
      resortNotices,
      exitingNoticeIds,
      triggerResortNotice,
      cancelResortNotice,
      cancelAllResortNotices,
    ]
  );
}
